import * as React from 'react';
import { useMemo } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { Heading, Modal, ModalContent } from '@restworld/ui-ds-base';

import {
  currentCompanyActionAtom,
  isCompanyForeignAtom,
  isEmployerCompanyActionModalOpenAtom,
} from '../../employer-companies.atoms';
import { ModalContentWrapper } from '../../employer-companies.styled';
import CompanyTypeChoice from './company-type-choice';
import CompanySearch from './company-search';
import CompanyDetailsForm from './company-details-form';
import { EmployerCompanyModalFooterContext } from './employer-company-modal-footer-context';

function EmployerCompanyActionModal() {
  const { t } = useTranslation('payment-and-invoices');
  const [isEmployerCompanyActionModalOpen, setIsEmployerCompanyActionModalOpen] = useAtom(
    isEmployerCompanyActionModalOpenAtom
  );
  const [currentCompanyAction, setCurrentCompanyAction] = useAtom(currentCompanyActionAtom);
  const setIsCompanyForeign = useSetAtom(isCompanyForeignAtom);
  const isChoosingCompanyType = useMemo(() => currentCompanyAction === 'chooseCompanyType', [currentCompanyAction]);
  const isSearchingCompany = useMemo(() => currentCompanyAction === 'searchCompany', [currentCompanyAction]);
  const [footer, setFooter] = React.useState<React.ReactNode>(null);

  const handleClose = React.useCallback(() => {
    setFooter(null);
    setIsEmployerCompanyActionModalOpen(false);
    // Reset state for next open
    setCurrentCompanyAction('chooseCompanyType');
    setIsCompanyForeign(false);
  }, [setIsEmployerCompanyActionModalOpen, setCurrentCompanyAction, setIsCompanyForeign]);

  const footerContextValue = React.useMemo(() => ({ setFooter }), []);

  return (
    <Modal open={isEmployerCompanyActionModalOpen} onOpenChange={(v) => !v && handleClose()}>
      <ModalContent
        className="px-4 md:w-full md:px-0 md:py-0 md:max-w-[95%] md:max-h-[95%] md:h-[95vh] xl:max-w-[1200px] xl:max-h-[650px]"
        noPadding
        // Header via `title` (not a custom header inside children): the DS renders title + footer in
        // fixed slots and only the body (children) scrolls. A header placed in children lives inside
        // the scrollable body and scrolls away. The DS close button is wired to onClose -> handleClose.
        title={
          <Heading color="neutral" className="text-lg">
            {currentCompanyAction === 'editCompany' ? t('edit_company') : t('new_company')}
          </Heading>
        }
        footer={footer}
      >
        <EmployerCompanyModalFooterContext.Provider value={footerContextValue}>
          <ModalContentWrapper>
            {isChoosingCompanyType ? (
              <CompanyTypeChoice />
            ) : isSearchingCompany ? (
              <CompanySearch />
            ) : (
              <CompanyDetailsForm />
            )}
          </ModalContentWrapper>
        </EmployerCompanyModalFooterContext.Provider>
      </ModalContent>
    </Modal>
  );
}

export default EmployerCompanyActionModal;
